import { useEffect, useMemo, useState } from "react";
import QuestionField from "./QuestionField";
import type { AssignmentDef } from "../content/assignments";
import {
  fetchAssignmentAnswers,
  fetchAssignmentStatus,
  saveAssignment,
  type AnswerMap,
} from "../lib/submissions";
import type {
  AssignmentStatusValue,
  GradeStatusValue,
} from "../lib/types";

// Presents an assignment's questions and captures answers. Supports save-as-draft
// and submit-final; reads back the student's prior answers + status on mount.
export default function AssignmentForm({
  enrollmentId,
  lessonId,
  assignment,
}: {
  enrollmentId: string;
  lessonId: string;
  assignment: AssignmentDef;
}) {
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [status, setStatus] = useState<AssignmentStatusValue | null>(null);
  const [gradeStatus, setGradeStatus] = useState<GradeStatusValue | null>(null);
  const [autoPercent, setAutoPercent] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Load prior answers + status (read-back).
  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      try {
        const [prior, st] = await Promise.all([
          fetchAssignmentAnswers(enrollmentId, lessonId),
          fetchAssignmentStatus(enrollmentId, lessonId),
        ]);
        if (!active) return;
        setAnswers(prior);
        setStatus(st?.status ?? null);
        setGradeStatus(st?.grade_status ?? null);
        setAutoPercent(st?.auto_percent ?? null);
      } catch (e) {
        if (active)
          setError(e instanceof Error ? e.message : "Failed to load your answers.");
      } finally {
        if (active) setLoading(false);
      }
    }
    void load();
    return () => {
      active = false;
    };
  }, [enrollmentId, lessonId]);

  const submitted = status === "submitted";

  const answeredCount = useMemo(
    () =>
      assignment.questions.filter((q) => {
        const v = answers[q.id];
        return v !== undefined && v !== null && v !== "";
      }).length,
    [answers, assignment.questions],
  );

  function setAnswer(qid: string, value: AnswerMap[string]) {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
    setNotice(null);
  }

  async function persist(next: AssignmentStatusValue) {
    setSaving(true);
    setError(null);
    setNotice(null);
    const res = await saveAssignment({
      enrollmentId,
      lessonId,
      questions: assignment.questions,
      answers,
      status: next,
    });
    setSaving(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    setStatus(next);
    if (next === "submitted" && res.gradeStatus) {
      setGradeStatus(res.gradeStatus ?? null);
      setAutoPercent(res.autoPercent ?? null);
      setNotice("Submitted and graded.");
    } else {
      setGradeStatus(null);
      setAutoPercent(null);
      setNotice("Draft saved.");
    }
  }

  if (loading) {
    return <p className="text-slate-500">Loading your answers…</p>;
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <StatusBadge status={status} gradeStatus={gradeStatus} />
        <span className="text-sm text-slate-500">
          {answeredCount} / {assignment.questions.length} answered
        </span>
      </div>

      {submitted && (
        <GradeResultBanner
          gradeStatus={gradeStatus}
          autoPercent={autoPercent}
        />
      )}

      <div className="space-y-4">
        {assignment.questions.map((q, i) => (
          <QuestionField
            key={q.id}
            question={q}
            index={i}
            value={answers[q.id] ?? null}
            disabled={submitted || saving}
            onChange={(v) => setAnswer(q.id, v)}
          />
        ))}
      </div>

      {error && (
        <div className="mt-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {notice && (
        <div className="mt-4 rounded border border-sky-200 bg-sky-50 p-3 text-sm text-sky-800">
          {notice}
        </div>
      )}

      {!submitted && (
        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={() => void persist("draft")}
            disabled={saving}
            className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save draft"}
          </button>
          <button
            onClick={() => void persist("submitted")}
            disabled={saving || answeredCount === 0}
            className="rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-60"
          >
            {saving ? "Submitting…" : "Submit workbook"}
          </button>
        </div>
      )}
    </div>
  );
}

function StatusBadge({
  status,
  gradeStatus,
}: {
  status: AssignmentStatusValue | null;
  gradeStatus: GradeStatusValue | null;
}) {
  if (status === "submitted") {
    // Once submitted, prefer the grade state for the badge.
    if (gradeStatus === "passed") {
      return (
        <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-800">
          Passed
        </span>
      );
    }
    if (gradeStatus === "failed") {
      return (
        <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-800">
          Not passed
        </span>
      );
    }
    if (gradeStatus === "needs_manual_grade") {
      return (
        <span className="rounded-full bg-sky-100 px-3 py-1 text-xs font-medium text-sky-800">
          Pending review
        </span>
      );
    }
    return (
      <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-800">
        Submitted
      </span>
    );
  }
  if (status === "draft") {
    return (
      <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800">
        Draft saved
      </span>
    );
  }
  return (
    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
      Not started
    </span>
  );
}

// Student-facing result banner shown after submit: auto score %, pass/fail, and
// a pending-instructor-review note when free_response items await manual grading.
function GradeResultBanner({
  gradeStatus,
  autoPercent,
}: {
  gradeStatus: GradeStatusValue | null;
  autoPercent: number | null;
}) {
  const pct = autoPercent != null ? `${autoPercent}%` : "—";

  if (gradeStatus === "passed") {
    return (
      <div className="mb-4 rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
        <strong>Passed.</strong> Auto-graded score: {pct} (pass mark 80%). Answers
        are locked.
      </div>
    );
  }
  if (gradeStatus === "failed") {
    return (
      <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        <strong>Not passed yet.</strong> Auto-graded score: {pct} — below the 80%
        pass mark. Talk to your instructor about a retake.
      </div>
    );
  }
  if (gradeStatus === "needs_manual_grade") {
    return (
      <div className="mb-4 rounded border border-sky-200 bg-sky-50 p-3 text-sm text-sky-800">
        <strong>Submitted.</strong> Auto-graded score: {pct} (pass mark 80%). Your
        scenario / short-answer responses are awaiting instructor review before
        this assignment is marked complete.
      </div>
    );
  }
  // Submitted with no grade computed yet (e.g. legacy row pre-grading).
  return (
    <div className="mb-4 rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
      You have submitted this workbook. Answers are locked pending instructor
      review.
    </div>
  );
}
